import PropTypes from 'prop-types';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Image } from 'Components/Image';
import { Tooltip } from 'Components/PortalTooltip';

import { srcOrNextImageProp } from 'PropTypes/imageProps';

import { FRAGRANCE_FREE } from 'assets/content/haircareFragrances';
import { notRecommended, recommended } from 'assets/content/skincareTextures';

import { Typography } from './Typography';

const DEFAULT_IMAGE_WIDTH = 100;
const DEFAULT_IMAGE_HEIGHT = 125;

const TooltipWrapper = styled.div`
  position: absolute;
  top: 37px;
  right: 42px;
`;

const Wrapper = styled.button`
  all: unset;
  box-sizing: border-box;

  position: relative;

  display: flex;
  flex-direction: column;
  margin: ${legacyTheme.spacing.s16} 0;
  width: 100%;
  max-width: 360px;
  height: min-content;
  min-height: 127px;
  align-items: center;

  text-align: left;
  font-family: ${legacyTheme.typography.simplonNorm};
  cursor: pointer;

  ${legacyTheme.breakpoints.up('md')} {
    margin: ${legacyTheme.spacing.s8};
  }
`;

const Label = styled.div`
  font-family: ${legacyTheme.typography.simplonNorm};
  font-size: 14px;
  color: ${({ selected }) => (selected ? theme.colors.neutral[100] : theme.colors.primary[400])};
  line-height: 21px;
`;

const DescriptionWrapper = styled.div`
  font-size: 14px;
  line-height: ${legacyTheme.typography.lineHeightNormal};
  letter-spacing: ${legacyTheme.typography.letterSpacingExtra};
  text-align: left;
  color: ${({ selected }) => (selected ? theme.colors.neutral[600] : theme.colors.neutral[800])};
  padding-right: 0px;
`;

const Description = styled.div`
  font-size: 11px;
  margin: ${legacyTheme.spacing.s8} 0;
`;

const TextWrapper = styled.div`
  display: flex;
  align-items: center;
  min-height: ${({ hasImage }) => (hasImage ? '53px' : 'auto')};
`;

const MarginBox = styled.div`
  margin: 0px 24px;
`;

const CrossedOutBox = styled.div`
  width: 100px;
  height: 100%;
  border-right: ${({ hasImageBorder }) =>
    hasImageBorder ? `${theme.colors.primary[400]} solid 1px` : 'none'};
  &::after {
    position: absolute;
    border-bottom: 1px solid ${theme.colors.primary[400]};
    width: 160px;
    transform: rotate(51deg);
    transform-origin: left;
    content: '';
  }
`;

const CrossOutImg = styled(Image)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-55%, -50%);
`;

const LimitedEdition = styled.div`
  position: absolute;
  right: 0px;
  color: ${theme.colors.neutral[100]};
  font-family: ${legacyTheme.typography.simplonMono};
  font-size: 9px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  top: 100%;
  width: 125px;
  transform: rotate3d(0, 0, 1, 90deg);
  transform-origin: top right;
  background-color: ${theme.colors.primary[400]};
`;

const RecommendedReasonContainer = styled.div`
  display: grid;
  align-items: center;
  width: 100%;
  padding: ${legacyTheme.spacing.s16};
  border-top: 0;
  background-color: transparent;
`;

const RecommendedReason = styled(Typography)`
  margin-top: 4px;
  color: ${({ selected }) => (selected ? theme.colors.neutral[100] : theme.colors.neutral[900])};
`;

const CardContent = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  transition: background-color, border-color;
  transition-duration: 300ms;
  background: ${({ selected }) =>
    selected ? theme.colors.primary[400] : theme.colors.neutral[100]};

  border: solid 1px
    ${({ selected }) => (selected ? theme.colors.primary['300'] : theme.colors.primary[400])};

  border-color: ${({ nonSelected }) => nonSelected && theme.colors.neutral['600']};
  box-shadow: ${({ nonSelected }) => nonSelected && `2px 2px 3px 0 ${theme.colors.neutral['600']}`};
`;

const RecommendationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  width: 100%;

  background-color: ${({ isRecommended }) =>
    isRecommended ? theme.colors.success[100] : theme.colors.neutral['700']};
  border: 1px solid ${theme.colors.primary[400]};
  border-bottom: none;

  color: ${theme.colors.neutral[900]};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CurrentChoice = styled(Typography)`
  font-size: 10px;
  color: ${theme.colors.secondary[300]};
`;

const Recommendation = styled(Typography)`
  position: relative;
  top: 1px;
  font-size: 10px;
`;

const FragranceImgContainer = styled.div`
  position: relative;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  background: ${theme.colors.neutral[100]};
`;

const DescriptionSubText = styled(Description)`
  /* when limited edition is displayed on the side shrink the text to avoid overlay */
  width: ${({ isLimitedEdition }) => (isLimitedEdition ? 170 : 200)}px;
  font-size: 12px;
  margin-bottom: 0;
  letter-spacing: 0.5px;
`;
const TrayCard = ({
  dataTestId,
  description,
  hasSelection,
  height,
  imageUrl,
  imageAlt,
  details,
  ingredients,
  isCurrentChoice,
  label,
  limitedEdition,
  onClick,
  isRecommended,
  isNotRecommended,
  recommendedReason,
  selected,
  tooltip,
  width,
  isFragrance,
  hasImageBorder,
}) => {
  const descriptionSubtext = ingredients || details;
  return (
    <Wrapper
      aria-label={`Select ${label} fragrance (disables other selections)`}
      data-testid={dataTestId}
      onClick={onClick}
      selected={selected}
      type="button"
    >
      {(isRecommended || isNotRecommended) && (
        <RecommendationWrapper isRecommended={isRecommended}>
          <Recommendation variant="mono3">
            {isRecommended ? recommended : notRecommended}
          </Recommendation>
        </RecommendationWrapper>
      )}
      <CardContent nonSelected={hasSelection && !selected} selected={selected}>
        {imageUrl && (
          <FragranceImgContainer height={height} width={width}>
            {label === FRAGRANCE_FREE.name ? (
              <CrossedOutBox hasImageBorder={hasImageBorder}>
                <CrossOutImg alt={imageAlt ?? label} height="60" src={imageUrl} width="40" />
              </CrossedOutBox>
            ) : (
              <Image
                alt={imageAlt ?? label}
                data-testid={`${dataTestId}-img`}
                height={height}
                src={imageUrl.src}
                width={width}
              />
            )}
          </FragranceImgContainer>
        )}

        <TextWrapper hasImage={imageUrl}>
          <MarginBox>
            {isCurrentChoice && (
              <CurrentChoice selected={selected} variant="mono3">
                Your current {isFragrance ? 'fragrance' : 'texture'}
              </CurrentChoice>
            )}
            <Label data-testid={`${dataTestId}-label`} selected={selected}>
              {label === 'NONE' ? (
                <>
                  I’d prefer fragrance-free
                  <br />
                  formulas
                </>
              ) : (
                label
              )}
            </Label>

            <DescriptionWrapper selected={selected}>
              {description && <Description>{description}</Description>}
              {descriptionSubtext && (
                <DescriptionSubText isLimitedEdition={limitedEdition}>
                  {descriptionSubtext}
                </DescriptionSubText>
              )}
            </DescriptionWrapper>
          </MarginBox>

          {tooltip && (
            <TooltipWrapper>
              <Tooltip
                arrowShift={100}
                content={tooltip.content}
                dataTestid={label}
                maxWidth={305}
                openerComponent={tooltip.icon}
              />
            </TooltipWrapper>
          )}
        </TextWrapper>

        {limitedEdition && <LimitedEdition>limited edition</LimitedEdition>}
      </CardContent>
      {recommendedReason && (
        <RecommendedReasonContainer selected={selected}>
          <RecommendedReason align="center" variant="p3">
            {recommendedReason}
          </RecommendedReason>
        </RecommendedReasonContainer>
      )}
    </Wrapper>
  );
};

TrayCard.propTypes = {
  description: PropTypes.string,
  hasSelection: PropTypes.bool,
  imageUrl: srcOrNextImageProp,
  imageAlt: PropTypes.string,
  details: PropTypes.string,
  ingredients: PropTypes.string,
  isCurrentChoice: PropTypes.bool,
  label: PropTypes.string.isRequired,
  limitedEdition: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  selected: PropTypes.bool.isRequired,
  recommendedReason: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  isRecommended: PropTypes.bool,
  isNotRecommended: PropTypes.bool,
  tooltip: PropTypes.shape({ content: PropTypes.string.isRequired, icon: PropTypes.node }),
  width: PropTypes.number,
  height: PropTypes.number,
  dataTestId: PropTypes.string,
  isFragrance: PropTypes.bool,
  hasImageBorder: PropTypes.bool,
};

TrayCard.defaultProps = {
  dataTestId: null,
  description: null,
  hasImageBorder: false,
  hasSelection: false,
  imageUrl: null,
  imageAlt: null,
  details: null,
  ingredients: null,
  isCurrentChoice: false,
  limitedEdition: false,
  isRecommended: false,
  isNotRecommended: false,
  recommendedReason: null,
  tooltip: null,
  width: DEFAULT_IMAGE_WIDTH,
  height: DEFAULT_IMAGE_HEIGHT,
  isFragrance: false,
};

export default TrayCard;
